import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";
import { customerDetailsSchema } from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import {
	availableCustomersAtom,
	userCustomersQueryFailedAtom,
} from "~/shared/state/user-profile-state.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

const getUserCustomers = async (customerIds: string[]) => {
	const searchParams = new URLSearchParams();
	for (const id of customerIds) {
		searchParams.append("id", id);
	}

	const response = await getAndValidate(
		`${apiRoutes.CUSTOMERSBYIDS}?${searchParams.toString()}`,
		customerDetailsSchema.array(),
	);
	if (!response.success) {
		throw new Error("Failed to fetch user customers");
	}
	return response.data;
};

export const useGetUserCustomers = () => {
	const [, setAvailableCustomers] = useAtom(availableCustomersAtom);
	const [hasFailed, setHasFailed] = useAtom(userCustomersQueryFailedAtom);
	const { customerIds } = useIsAuthenticated();
	const { isInternal } = useDeploymentType();

	const isEnabled = customerIds.length > 0 && !isInternal && !hasFailed;

	const query = useQuery({
		queryKey: ["user-customers", customerIds],
		queryFn: (): Promise<CustomerDetails[]> => getUserCustomers(customerIds),
		enabled: isEnabled,
		retry: false,
		// Once the query has an answer (data or error), don't refetch or retry
		// when a new subscriber mounts. Without this, an errored query (e.g. a
		// vendor user hitting 403) flips status `error` -> `pending` on every
		// new subscribe, which flips `isLoading` to true and causes any
		// `isLoading`-gated parent provider to swap children for a spinner --
		// producing a remount/refetch loop visible as page flicker.
		refetchOnMount: false,
		retryOnMount: false,
	});

	useEffect(() => {
		if (query.data) {
			setAvailableCustomers(query.data);
		}
		if (query.error) {
			setHasFailed(true);
		}
	}, [query.data, query.error, setAvailableCustomers, setHasFailed]);

	return {
		data: query.data,
		isLoading: query.isLoading,
		error: query.error,
	};
};
